import {
  Box,
  CardContent,
  Paper,
  Tabs,
  Typography,
  Card,
  Divider,
  useTheme,
} from "@mui/material";
import React from "react";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { formatFeatureCode } from "../../utils/addon-utils";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { getCategoryIcon } from "../../utils/addon-utils";
import { alpha } from "@mui/material/styles";
import { ArtButton } from "@art/shared-ui";
import { Grid } from "@mui/material";
import { Tab } from "@mui/material";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`addon-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function AvailableAddons({
  addonGroups,
  activeTab,
  setActiveTab,
  handleSimulateAddonLocal,
  canPurchase,
}: any) {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <ShoppingCartOutlinedIcon
          sx={{ fontSize: 22, color: "primary.main" }}
        />
        <Typography variant="h6" fontWeight={600}>
          Available Add-ons
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {canPurchase
          ? "Choose an add-on to increase specific limits on your current plan. Costs are prorated to your billing cycle."
          : "You can view available add-ons. Ask a tenant administrator for payment permission to purchase one."}
      </Typography>

      {addonGroups.length > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {addonGroups.map((group: any, idx: number) => (
                <Tab
                  key={idx}
                  label={group.featureName}
                  icon={getCategoryIcon(group.featureCode)}
                  iconPosition="start"
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    minHeight: 48,
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {addonGroups.map((group: any, index: number) => (
            <CustomTabPanel value={activeTab} index={index} key={index}>
              <Grid container spacing={3}>
                {group.addons.map((addon: any) => (
                  <Grid item xs={12} sm={6} md={4} key={addon.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          boxShadow: `0 6px 24px ${alpha(
                            theme.palette.primary.main,
                            0.12,
                          )}`,
                          transform: "translateY(-3px)",
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2.5 } }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={{ xs: 1, sm: 1.5 }}
                          mb={1.5}
                        >
                          <Box
                            sx={{
                              width: { xs: 32, sm: 40 },
                              height: { xs: 32, sm: 40 },
                              flexShrink: 0,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: "primary.main",
                              "& .MuiSvgIcon-root": {
                                fontSize: { xs: 18, sm: 22 },
                              },
                            }}
                          >
                            {getCategoryIcon(addon.feature?.code)}
                          </Box>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: "break-word" }}>
                            {addon.name.toUpperCase()}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                        >
                          +{addon.limit_grant}{" "}
                          {formatFeatureCode(addon.feature?.code) || ""}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={2}
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {addon.description ||
                            `Extends ${addon.feature?.name || "feature"} limit`}
                        </Typography>
                      </CardContent>

                      <Divider />

                      <Box
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "stretch", sm: "center" },
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 1.5, sm: 0 },
                          "& button": {
                            width: { xs: "100%", sm: "auto" }
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            color="primary.main"
                            lineHeight={1.2}
                          >
                            {addon.currency} {addon.price?.toFixed?.(2) || addon.price}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            per {addon.billing_interval || "month"}
                          </Typography>
                        </Box>
                        {canPurchase && <ArtButton
                          variant="contained"
                          color="primary"
                          label="Purchase"
                          size="small"
                          onClick={() => handleSimulateAddonLocal(addon)}
                        />}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CustomTabPanel>
          ))}
        </>
      ) : (
        <Box
          sx={{
            py: 5,
            textAlign: "center",
            borderRadius: 2,
            bgcolor: alpha(theme.palette.action.hover, 0.3),
          }}
        >
          <WidgetsOutlinedIcon
            sx={{
              fontSize: 44,
              color: "text.disabled",
              mb: 1,
            }}
          />
          <Typography variant="body2" color="text.secondary">
            No add-ons available at the moment.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Check back later for new add-ons to enhance your plan.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default AvailableAddons;
